import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	Output,
	SimpleChanges,
	ViewChild
} from '@angular/core';
import { AdAccountListItem } from '@brand-lolly/app/_models/brand-instagram-integration.model';
import {
	LoAvatar,
	LoButton,
	LoChip,
	LoToggle,
	LoTooltip
} from 'lo-design-system';

@Component({
	standalone: true,
	selector: 'meta-ad-account-row',
	imports: [LoAvatar, LoButton, LoChip, LoToggle, LoTooltip],
	template: `
		<div class="ad-acc">
			<lo-avatar
				[name]="account.name"
				[src]="account.business_picture_url || ''"
			></lo-avatar>
			<div class="main">
				<div class="title">
					<span class="lo-type-text-md-semibold">
						{{ account.name }}
					</span>
					<lo-chip>{{ account.currency }}</lo-chip>
				</div>
				<div class="sub lo-type-text-xs">
					<span class="muted">{{ account.id }}</span>
					<span class="muted">
						{{ account.business_name || '—' }}
					</span>
					<span class="muted">{{ campaignsLabel }}</span>
				</div>
			</div>
			<div
				class="toggle"
				[loTooltip]="toggleTooltip"
				[loTooltipDisabled]="!toggleTooltip"
			>
				<lo-toggle
					[ariaLabel]="'Activate ' + account.name"
					[value]="account.is_consumed"
					[disabled]="!canToggle || toggleLoading"
					(valueChange)="onToggle()"
				></lo-toggle>
			</div>
			<button
				lo-button
				variant="link"
				size="sm"
				type="button"
				(click)="manage.emit(account)"
			>
				Manage
			</button>
		</div>
	`,
	styles: [
		`
			:host {
				display: block;

				.ad-acc {
					display: flex;
					align-items: center;
					gap: var(--lo-space-gap-md);
					padding-top: var(--lo-space-gap-md);
					border-top: 1px solid var(--lo-color-border-default);

					.main {
						display: flex;
						flex: 1;
						flex-direction: column;
						gap: var(--lo-space-gap-xs);
						min-width: 0;

						.title {
							display: flex;
							align-items: center;
							gap: var(--lo-space-gap-sm);
						}

						.sub {
							display: flex;
							align-items: center;
							flex-wrap: wrap;
							gap: var(--lo-space-gap-sm);

							.muted {
								color: var(--lo-color-text-secondary);
							}
						}
					}
				}
			}

			:host(:first-of-type) .ad-acc {
				padding-top: 0;
				border-top: none;
			}
		`
	]
})
export class MetaAdAccountRowComponent implements OnChanges {
	@Input({ required: true }) account!: AdAccountListItem;
	@Input() remainingSlots: number | null = null;
	@Input() toggleLoading = false;

	@Output() toggle = new EventEmitter<AdAccountListItem>();
	@ViewChild(LoToggle) private toggleControl?: LoToggle;

	@Output() manage = new EventEmitter<AdAccountListItem>();

	get canToggle(): boolean {
		if (this.account.is_consumed) return true;
		if (this.remainingSlots === null) return true;
		return this.remainingSlots > 0;
	}

	get toggleTooltip(): string {
		if (this.canToggle || this.account.is_consumed) return '';
		return 'Plan limit reached — release another ad account first';
	}

	get campaignsLabel(): string {
		const count = this.account.campaigns_count;
		if (count === 0) return 'No campaigns';
		return `${count} campaign${count === 1 ? '' : 's'}`;
	}

	ngOnChanges(changes: SimpleChanges): void {
		// TRAP: the switch flips itself on a press and the parent only learns
		// the outcome when the request ends, so a refused change (plan limit,
		// a release blocked by live campaigns) would leave it standing on the
		// wrong side. Put it back on what the account really is once the
		// request is over.
		if (changes['toggleLoading'] && !this.toggleLoading) {
			this.toggleControl?.value.set(this.account.is_consumed);
		}
	}

	onToggle(): void {
		if (!this.canToggle || this.toggleLoading) return;
		this.toggle.emit(this.account);
	}
}
